import { useState, useCallback, useEffect, useRef } from 'react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'

const KAMPALA_CENTER = { lat: 0.3476, lng: 32.5825 }

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px'
}

const defaultMapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
}

export default function MapPicker({ 
  onLocationSelect, 
  initialLat, 
  initialLng,
  initialAddress = '' 
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || import.meta.env?.VITE_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
    version: 'weekly',
  })

  const [marker, setMarker] = useState(
    initialLat && initialLng
      ? { lat: initialLat, lng: initialLng }
      : KAMPALA_CENTER
  )
  
  const [searchQuery, setSearchQuery] = useState(initialAddress)
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState(initialAddress)
  const [searchError, setSearchError] = useState('')
  
  const searchInputRef = useRef(null)
  const autocompleteSessionTokenRef = useRef(null)
  const mapRef = useRef(null)

  const getAutocompleteSessionToken = useCallback(async () => {
    if (!autocompleteSessionTokenRef.current) {
      const { AutocompleteSessionToken } = await window.google.maps.importLibrary('places')
      autocompleteSessionTokenRef.current = new AutocompleteSessionToken()
    }
    return autocompleteSessionTokenRef.current
  }, [])

  // Handle search input changes
  const handleSearchChange = useCallback(async (e) => {
    const query = e.target.value
    setSearchQuery(query)
    setSelectedAddress('')
    setSearchError('')

    if (!query.trim()) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    try {
      const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places')
      const sessionToken = await getAutocompleteSessionToken()
      const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        includedRegionCodes: ['ug'],
        language: 'en',
        region: 'ug',
        sessionToken,
      })
      
      const nextSuggestions = (response?.suggestions || [])
        .filter((suggestion) => suggestion.placePrediction)
        .map((suggestion) => ({
          id: suggestion.placePrediction.placeId,
          description: suggestion.placePrediction.text?.toString() || '',
          placePrediction: suggestion.placePrediction,
        }))

      setSuggestions(nextSuggestions)
      setShowSuggestions(nextSuggestions.length > 0)
    } catch (error) {
      console.error('Error getting predictions:', error)
      setSuggestions([])
      setShowSuggestions(false)
      setSearchError('Location search is not available for this API key. You can still click the map to choose a location.')
    }
  }, [getAutocompleteSessionToken])

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback(async (suggestion) => {
    if (!suggestion?.placePrediction || !mapRef.current) return

    try {
      const place = suggestion.placePrediction.toPlace()
      await place.fetchFields({ fields: ['location', 'formattedAddress', 'displayName'] })

      if (!place.location) return

      const lat = typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat
      const lng = typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng
      const address = place.formattedAddress || suggestion.description

      setMarker({ lat, lng })
      setSearchQuery(address)
      setSelectedAddress(address)
      setSuggestions([])
      setShowSuggestions(false)
      setSearchError('')
      autocompleteSessionTokenRef.current = null

      mapRef.current?.panTo({ lat, lng })
      mapRef.current?.setZoom(15)

      onLocationSelect(lat, lng, address)
    } catch (error) {
      console.error('Error getting place details:', error)
      setSearchError('We could not load that place. Try another search or click the map directly.')
    }
  }, [onLocationSelect])

  // Handle map click
  const handleMapClick = useCallback(async (e) => {
    if (!e.latLng) return
    
    const lat = e.latLng.lat()
    const lng = e.latLng.lng()
    setMarker({ lat, lng })

    // Reverse geocode to get address
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || import.meta.env?.VITE_GOOGLE_MAPS_API_KEY}`
      )
      const data = await response.json()
      const address = data.results[0]?.formatted_address || ''

      setSearchQuery(address)
      setSelectedAddress(address)
      onLocationSelect(lat, lng, address)
    } catch (error) {
      console.error('Error reverse geocoding:', error)
      onLocationSelect(lat, lng, '')
    }
  }, [onLocationSelect])

  // Save map instance ref
  const onMapLoad = useCallback((map) => {
    mapRef.current = map
  }, [])

  // Handle clicking outside suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (loadError) {
    return (
      <div className="w-full">
        <div className="w-full h-[300px] rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
          Error loading maps. Please check your internet connection.
        </div>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="w-full">
        <div className="w-full h-[300px] rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
          Loading map...
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Search Input */}
      <div className="relative mb-3" ref={searchInputRef}>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder="Search for an address..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {searchError && (
          <p className="text-xs text-red-600 mt-1 text-left">
            {searchError}
          </p>
        )}
        
        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-48 overflow-y-auto shadow-lg">
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.id}
                onClick={() => handleSelectSuggestion(suggestion)}
                className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
              >
                {suggestion.description}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={marker}
        zoom={13}
        onClick={handleMapClick}
        onLoad={onMapLoad}
        options={defaultMapOptions}
      >
        <Marker position={marker} />
      </GoogleMap>

      {/* Helper Text */}
      <p className="text-sm text-gray-500 mt-2">
        {selectedAddress ? (
          <span className="text-green-600">Location selected: {selectedAddress}</span>
        ) : (
          'Click on the map or search for an address to select a location'
        )}
      </p>
    </div>
  )
}
